import LeadForm from "@/components/LeadForm";
import { Award, CheckCircle2, Building2, Briefcase, GraduationCap, HandshakeIcon, Users, Target, DollarSign } from "lucide-react";

function HeroBlock({ label, title, subtitle, image }) {
  return (
    <section className="pt-12 pb-16 bg-gradient-to-br from-neutral-50 to-sky-50">
      <div className="container-eq grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="label-eq text-sky-500 mb-3">{label}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter">{title}</h1>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">{subtitle}</p>
        </div>
        {image && <img src={image} alt={title} className="rounded-3xl h-[380px] w-full object-cover" />}
      </div>
    </section>
  );
}

function FeatureGrid({ items }) {
  return (
    <section className="py-16 container-eq grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((i, k) => (
        <div key={k} className="p-6 card-eq">
          <div className="w-11 h-11 rounded-xl bg-neutral-950 flex items-center justify-center mb-3">
            <i.icon className="w-5 h-5 text-sky-400" />
          </div>
          <div className="font-display font-bold text-lg">{i.title}</div>
          <p className="text-sm text-neutral-600 mt-2">{i.desc}</p>
        </div>
      ))}
    </section>
  );
}

function CTABlock({ heading, source }) {
  return (
    <section className="py-16 bg-neutral-950 text-white">
      <div className="container-eq grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h3 className="font-display font-bold text-3xl">{heading}</h3>
          <p className="text-neutral-400 mt-3">Talk to our team — we'll walk you through everything.</p>
        </div>
        <div className="bg-white text-neutral-900 rounded-3xl p-6">
          <LeadForm source={source} compact />
        </div>
      </div>
    </section>
  );
}

export function CorporatePage() {
  return (
    <div data-testid="corporate-page">
      <HeroBlock label="Corporate Training" title="Upskill your team with AI marketing" subtitle="Custom workshops and multi-week programs for marketing teams and business leaders."
        image="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200" />
      <FeatureGrid items={[
        { icon: Users, title: "Custom Curriculum", desc: "Modules tailored to your industry and team's skill gaps." },
        { icon: Briefcase, title: "On-site or Virtual", desc: "Delivered at your office or via live remote sessions." },
        { icon: Target, title: "Outcome-driven", desc: "KPIs aligned with your revenue and growth targets." },
        { icon: Award, title: "Certifications", desc: "Team members earn industry-recognized certificates." },
        { icon: Building2, title: "Fortune 500 Ready", desc: "Enterprise SLAs, security and reporting." },
        { icon: HandshakeIcon, title: "Ongoing Support", desc: "6-month post-training mentorship included." },
      ]} />
      <CTABlock heading="Request a corporate proposal" source="corporate_page" />
    </div>
  );
}

export function InternshipPage() {
  return (
    <div data-testid="internship-page">
      <HeroBlock label="Internships" title="Real client projects. Real experience." subtitle="Work on live campaigns with our AI marketing team and earn a certification + stipend."
        image="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200" />
      <FeatureGrid items={[
        { icon: Briefcase, title: "3-6 Month Programs", desc: "Structured internship with mentor-led weekly reviews." },
        { icon: DollarSign, title: "Paid Stipend", desc: "Monthly stipend for full-time interns." },
        { icon: Award, title: "Certificate", desc: "Verifiable completion certificate to boost your CV." },
        { icon: Users, title: "Live Projects", desc: "Contribute to actual paid client campaigns." },
        { icon: Target, title: "Skill Tracks", desc: "Choose SEO, Paid Ads, Content or Analytics." },
        { icon: HandshakeIcon, title: "PPO Chance", desc: "Top performers are offered full-time roles." },
      ]} />
      <CTABlock heading="Apply for the next internship batch" source="internship_page" />
    </div>
  );
}

export function PlacementPage() {
  return (
    <div data-testid="placement-page">
      <HeroBlock label="Placement Assistance" title="From classroom to career — we've got you." subtitle="100% placement support with resume prep, mock interviews and 50+ hiring partners."
        image="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200" />
      <FeatureGrid items={[
        { icon: Award, title: "Resume Build", desc: "1-on-1 sessions to craft a job-winning resume." },
        { icon: Users, title: "Mock Interviews", desc: "Simulated HR + technical interviews with feedback." },
        { icon: Building2, title: "50+ Partners", desc: "Direct referrals to our hiring partner network." },
        { icon: Target, title: "Job Portal", desc: "Access to our exclusive private job board." },
        { icon: HandshakeIcon, title: "Salary Negotiation", desc: "Guidance to land the best offer possible." },
        { icon: Briefcase, title: "Follow-through", desc: "Support until you sign the offer letter." },
      ]} />
      <CTABlock heading="Get placement guidance" source="placement_page" />
    </div>
  );
}

export function CertificationPage() {
  return (
    <div data-testid="certification-page">
      <HeroBlock label="Certification" title="Industry-recognized credentials" subtitle="Every EQtech program includes a verifiable completion certificate you can showcase on LinkedIn."
        image="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200" />
      <FeatureGrid items={[
        { icon: Award, title: "Verifiable", desc: "Each certificate has a unique verification URL." },
        { icon: CheckCircle2, title: "Recognized", desc: "Accepted by 50+ partner companies." },
        { icon: Target, title: "Skill-based", desc: "Awarded on passing project + assessment." },
      ]} />
      <CTABlock heading="Ask about certifications" source="certification_page" />
    </div>
  );
}

export function CareersPage() {
  return (
    <div data-testid="careers-page">
      <HeroBlock label="Careers @ EQtech" title="Join a team building the future of marketing education" subtitle="We're always looking for trainers, mentors, content creators and product folks who care."
        image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200" />
      <section className="py-16 container-eq">
        <div className="max-w-2xl">
          <h3 className="font-display font-bold text-2xl mb-4">Send us your resume</h3>
          <p className="text-neutral-600 mb-6">Even if you don't see an open role, drop us a note — we're growing fast.</p>
          <LeadForm source="careers_page" />
        </div>
      </section>
    </div>
  );
}
